// Drupal content is editorial HTML, not executable application markup.
const ALLOWED_TAGS = new Set('p br hr strong b em i u s del blockquote h2 h3 h4 h5 h6 ul ol li a img figure figcaption table thead tbody tfoot tr th td div span pre code sup sub video audio source iframe'.split(' '));
const DROP_TAGS = new Set(['script', 'style', 'object', 'embed', 'form', 'input', 'button', 'svg', 'math', 'template']);
const FRAME_HOSTS = ['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'player.vimeo.com', 'www.ivoox.com', 'ivoox.com', 'open.spotify.com', 'w.soundcloud.com', 'player.podigee.io'];

export function safeUrl(value) {
  if (typeof value !== 'string' || !value.trim() || Array.from(value).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return '';
  const url = value.trim();
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  if (/^https?:\/\//i.test(url)) {
    try { const parsed = new URL(url); return parsed.username || parsed.password ? '' : parsed.href; } catch (error) { return ''; }
  }
  return '';
}

export function mediaUrl(value) {
  const safe = safeUrl(value);
  if (!safe || safe[0] === '/') return '';
  const url = new URL(safe);
  if (['youtube.com', 'www.youtube.com', 'youtu.be'].includes(url.hostname)) {
    const id = url.hostname === 'youtu.be' ? url.pathname.slice(1) : url.searchParams.get('v') || url.pathname.replace(/^\/embed\//, '');
    return /^[\w-]+$/.test(id) ? `https://www.youtube-nocookie.com/embed/${id}` : '';
  }
  if (['vimeo.com', 'www.vimeo.com'].includes(url.hostname)) {
    const id = url.pathname.split('/').filter(Boolean).pop();
    return /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : '';
  }
  return FRAME_HOSTS.includes(url.hostname) ? safe.replace(/^http:/, 'https:') : '';
}

export function sanitizeReportHtml(value) {
  if (!value || typeof document === 'undefined') return '';
  const template = document.createElement('template');
  template.innerHTML = String(value);
  const clean = parent => {
    Array.from(parent.childNodes).forEach(node => {
      if (node.nodeType === 8) { node.remove(); return; }
      if (node.nodeType !== 1) return;
      const tag = node.tagName.toLowerCase();
      if (DROP_TAGS.has(tag)) { node.remove(); return; }
      clean(node);
      if (!ALLOWED_TAGS.has(tag)) { node.replaceWith(...Array.from(node.childNodes)); return; }
      const attrs = {};
      ['href', 'src', 'alt', 'title', 'colspan', 'rowspan', 'scope', 'poster'].forEach(name => { attrs[name] = node.getAttribute(name); });
      Array.from(node.attributes).forEach(attr => node.removeAttribute(attr.name));
      if (attrs.title) node.setAttribute('title', attrs.title);
      if (tag === 'a') {
        const href = safeUrl(attrs.href);
        if (href) { node.setAttribute('href', href); node.setAttribute('target', '_blank'); node.setAttribute('rel', 'noopener noreferrer'); }
      }
      if (['img', 'source', 'audio', 'video'].includes(tag)) {
        const src = safeUrl(attrs.src);
        if (src) node.setAttribute('src', src);
        if (tag === 'img') { node.setAttribute('alt', attrs.alt || ''); node.setAttribute('loading', 'lazy'); }
        if (tag === 'audio' || tag === 'video') { node.setAttribute('controls', ''); node.setAttribute('preload', 'none'); }
        if (tag === 'video' && safeUrl(attrs.poster)) node.setAttribute('poster', safeUrl(attrs.poster));
      }
      if (tag === 'iframe') {
        const src = mediaUrl(attrs.src);
        if (!src) { node.remove(); return; }
        node.setAttribute('src', src); node.setAttribute('loading', 'lazy'); node.setAttribute('title', attrs.title || 'Contenido multimedia');
        node.setAttribute('allowfullscreen', ''); node.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        node.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');
      }
      if (tag === 'td' || tag === 'th') {
        ['colspan', 'rowspan'].forEach(name => { if (/^\d{1,2}$/.test(attrs[name])) node.setAttribute(name, attrs[name]); });
        if (['row', 'col', 'rowgroup', 'colgroup'].includes(attrs.scope)) node.setAttribute('scope', attrs.scope);
      }
    });
  };
  clean(template.content);
  return template.innerHTML;
}

export function plainText(value) {
  if (typeof document === 'undefined') return String(value || '').replace(/<[^>]*>/g, '');
  const element = document.createElement('div');
  element.innerHTML = sanitizeReportHtml(value);
  return element.textContent || '';
}
